import { Button, CILabel, Input } from 'ccs-ui-components';
import { isArray } from 'lodash';
import { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ItemHandler from '../param-list-handler/ItemHandler';
import { nonSpaceCharacterRegex } from '../../../../../constants';
import { useServiceDetailForm } from '../../../../../hooks';
import { IQueryPathParam } from '../../../../../models';

import classes from './InputHandler.module.scss';
import { ServiceVariableTooltipVal } from '../../../../../utilities/services/ServiceVariableTooltipVal';

interface ItemListProps {
  listOfItems: (data: IQueryPathParam[]) => void;
  list?: IQueryPathParam[] | null;
  paramName?: string
}

const InputHandler = ({ listOfItems, list = [], paramName = '' }: ItemListProps) => {
  const { handleInputChange, formData, setFormParamValues } = useServiceDetailForm();
  const [paramList, setParamList] = useState<IQueryPathParam[] | null | undefined>(list);
  const [updateObject, setUpdateObject] = useState<IQueryPathParam>();
  const { t: translate } = useTranslation();

  // Enabling add button based on validity
  const isAddBtnEnabled = () =>
  (
    formData.paramKey.isValid
    && (formData.paramKey.value && (nonSpaceCharacterRegex.test(formData.paramKey.value)))
    && formData.paramValue.isValid
    && (formData.paramValue.value)
  );

  // On click of add button
  const onAddClickHandler = () => {
    const tempList = paramList && isArray(paramList) ? [...paramList] : [];
    const tempObj = {
      key: formData.paramKey.value,
      value: formData.paramValue.value,
      isEdit: false
    };
    tempList.push(tempObj);
    setParamList(tempList);
    formData.paramKey.value = '';
    formData.paramValue.value = '';
    listOfItems(tempList);
  };

  const SERVICE_VALIDATION_ERRORS = {
    keyExists: translate('service_validation_error.keyExists')
  };

  // Checks if key already exists
  useEffect(() => {
    const newFormData = { ...formData };
    let status = false;
    if (isArray(paramList)) {
      paramList.forEach((item) => {
        if (formData.paramKey.value === item.key) {
          status = true;
        }
        return item;
      });
    }
    if (status) {
      newFormData.paramKey.isValid = false;
      newFormData.paramKey.isTouched = false;
      newFormData.paramKey.error = SERVICE_VALIDATION_ERRORS.keyExists;
    } else if (
      !status
      && nonSpaceCharacterRegex.test(newFormData.paramKey.value)
    ) {
      newFormData.paramKey.isValid = true;
      newFormData.paramKey.isTouched = true;
      newFormData.paramKey.error = '';
    }
    setFormParamValues(newFormData);
  }, [
    formData.paramKey.value,
    paramList?.length,
    paramList
  ]);

  // On update of any item in list
  const onItemChange = (obj: IQueryPathParam) => {
    setUpdateObject(obj);
  };

  useEffect(() => {
    const tempIndex = updateObject?.index ? updateObject.index : 0;
    if (updateObject) {
      const tempList = paramList ? [...paramList] : [];
      tempList.splice(tempIndex, 1, updateObject);
      setParamList(tempList);
      listOfItems(tempList);
    }
  }, [updateObject]);

  // on delete of any item in list
  const onItemDelete = (index: number) => {
    const tempData = paramList ? [...paramList] : [];
    tempData.splice(index, 1);
    setParamList(tempData);
    listOfItems(tempData);
  };

  return (
    <Row className={classes['main-container']}>
      <Form.Group>
        <Row className={classes['param-container']}>
          <Row className={classes['param-header']}>
            <Col sm={5}>
              <CILabel labelText={String(translate('service_details_form_component.lbl_key'))} id="param-header" />
            </Col>
            <Col sm={6} className={classes['value-header']}>
              <CILabel labelText={String(translate('service_details_form_component.lbl_value'))} infoIconText={<ServiceVariableTooltipVal />} id="value-header" />
            </Col>
          </Row>
          <Row className={classes['param-body']}>
            <Col sm={5}>
              <Input
                name="paramKey"
                label=""
                data-testid="param-key"
                placeholder={String(translate(
                  'service_details_form_component.plchd_param_key'
                ))}
                id="paramKey"
                value={formData.paramKey.value}
                onChange={handleInputChange}
                error={!formData.paramKey.isValid}
                errorTxt={formData.paramKey.error}
                maxLength={Number.MAX_SAFE_INTEGER}
              />
            </Col>
            <Col sm={5}>
              <Input
                name="paramValue"
                label=""
                data-testid="param-value"
                placeholder={
                  String(translate(
                    'service_details_form_component.plchd_param_value'
                  ))
                }
                value={formData.paramValue.value}
                onChange={handleInputChange}
                id="paramValue"
                error={!formData.paramValue.isValid}
                errorTxt={formData.paramValue.error}
                maxLength={Number.MAX_SAFE_INTEGER}
              />
            </Col>
            <Col sm={2}>
              <Button
                className={classes['add-button']}
                data-testid="add-header"
                variant="secondary"
                onClick={onAddClickHandler}
                disabled={!isAddBtnEnabled()}
              >
                {translate('service_operation_form.add_button')}
              </Button>
            </Col>
          </Row>
          {paramList
            && paramList?.length >= 1
            && paramList !== null
            && paramList.map((item, index) => (
              <ItemHandler
                index={index}
                key={item.key}
                itemKey={item.key}
                itemValue={item.value}
                isEdit={item.isEdit}
                isView={false} // must be false as this is edit/create component
                updateItem={onItemChange}
                deleteItem={onItemDelete}
                itemList={paramList}
              />
            ))}
          {paramList && paramList?.length < 1 && (
            <div className={classes['no-data']} data-testid="no-data">
              {paramName !== ''
                ? translate('service_details_form_component.plchd_no_records', {
                  replace: { param: paramName }
                }) : translate('service_details_form_component.plchd_no_records')}
            </div>
          )}
        </Row>
      </Form.Group>
    </Row>
  );
};
export default InputHandler;
